/**
 * Device detection utilities for PWA mobile-first experience
 */

export interface DeviceInfo {
  isMobile: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  isTablet: boolean;
  userAgent: string;
}

/**
 * Detects the current device type and platform
 */
export function getDeviceInfo(): DeviceInfo {
  const userAgent = navigator.userAgent.toLowerCase();

  const isAndroid = /android/.test(userAgent);
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isTablet = /ipad/.test(userAgent)
    || (isAndroid && !/mobile/.test(userAgent))
    || (/tablet/.test(userAgent));

  // Enhanced mobile detection
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
    || (navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
    || window.matchMedia('(max-width: 768px)').matches;

  return {
    isMobile,
    isAndroid,
    isIOS,
    isTablet,
    userAgent
  };
}

/**
 * Checks if the device is a mobile phone (not tablet)
 */
export function isMobilePhone(): boolean {
  const deviceInfo = getDeviceInfo();
  return deviceInfo.isMobile && !deviceInfo.isTablet;
}

/**
 * Checks if PWA install is available on this platform
 */
export function isPWAInstallAvailable(): boolean {
  const deviceInfo = getDeviceInfo();

  // Android Chrome supports automatic PWA install
  if (deviceInfo.isAndroid) {
    return true;
  }

  // iOS Safari requires manual installation
  if (deviceInfo.isIOS) {
    return true;
  }

  // Desktop browsers may support PWA install
  return 'serviceWorker' in navigator && 'beforeinstallprompt' in window;
}

/**
 * Checks if the current platform supports automatic PWA install prompt
 */
export function supportsAutomaticPWAInstall(): boolean {
  const deviceInfo = getDeviceInfo();

  // Only Android Chrome and some desktop browsers support automatic install
  return deviceInfo.isAndroid && 'beforeinstallprompt' in window;
}

/**
 * Checks if the user is currently viewing the app in standalone PWA mode
 */
export function isRunningAsPWA(): boolean {
  // Check for standalone display mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // Check for iOS PWA mode
  const isIOSStandalone = 'standalone' in window.navigator
    && (window.navigator).standalone === true;

  return isStandalone || isIOSStandalone;
}

/**
 * Checks if we should show the mobile-first PWA screen
 */
export function shouldShowMobileFirstScreen(): boolean {
  const deviceInfo = getDeviceInfo();

  // Only show on mobile devices
  if (!isMobilePhone()) {
    return false;
  }

  // Don't show if already running as PWA
  if (isRunningAsPWA()) {
    return false;
  }

  // Don't show if user has previously dismissed (check localStorage)
  //   const dismissed = localStorage.getItem('pwa-mobile-first-dismissed');
  //   if (dismissed === 'true') {
  //     return false;
  //   }

  return true;
}

/**
 * Marks the mobile first screen as dismissed
 */
export function dismissMobileFirstScreen(): void {
  localStorage.setItem('pwa-mobile-first-dismissed', 'true');
}

/**
 * Resets the mobile first screen dismissal (for testing)
 */
export function resetMobileFirstScreenDismissal(): void {
  localStorage.removeItem('pwa-mobile-first-dismissed');
}
